// packages import
import { Box } from '@mui/material'
import { FC, Reducer, useContext, useEffect, useMemo, useReducer } from 'react'
// component block
import { Alert } from '../components/common/Alert'
import { EmptyData } from '../components/common/EmptyData'
import { SearchWithSort } from '../components/common/SearchWithSort'
import { UploadCsv } from '../components/common/UploadCsv'
import { EditUser } from '../components/users/EditUser'
import { InviteNewUser } from '../components/users/InviteNewUser'
import { UserListing } from '../components/users/UserListing'
// others
import {
  SearchUserStatusFilter,
  useFetchAllUsersLazyQuery,
  UserStatus,
  useSearchUserLazyQuery
} from '../../generated'
import { UserAction, UserActionType, userInitialState, userReducer, UserState } from '../components/users/userReducer'
import {
  GRAPHQL_QUERY_POLICY,
  GRAPHQL_QUERY_POLICY_CACHE_FIRST,
  NO_USER_FOUND,
  SEARCH_PLACEHOLDER,
  SEARCHABLE_USER_ROLES
} from '../constants'
import { AppContext } from '../context'
import { ActionType } from '../context/AppContextReducer'
import { flexCenter } from '../theme/styleConstants'
import { useDebounce } from 'use-debounce'

interface UserContainerProps {
  statusTab?: number // 0 = Active, 1 = Deactive, 2 = All
}

export const UserContainer: FC<UserContainerProps> = ({ statusTab = 0 }) => {
  const {
    userRoles,
    searchValue,
    dispatch: reducerDispatch,
    rolesData,
    statusFilter,
    dateFilter,
    toDateFilter,
    currentPage,
    pageLimit
  } = useContext(AppContext)
  const [userState, userDispatch] = useReducer<Reducer<UserState, UserAction>>(userReducer, userInitialState)
  const { usersData } = userState
  // Same list the Role filter's "All" option sends, so the default view matches "All"
  const roles = userRoles && userRoles.length > 0 ? userRoles : SEARCHABLE_USER_ROLES

  // Map statusTab to UserStatus: 0 = Active, 1 = Deactive, 2 = All (null)
  // For "All" tab, we pass null explicitly to show all users regardless of status
  // Memoize to prevent unnecessary recalculations
  const tabStatus = useMemo((): UserStatus | null | undefined => {
    if (statusTab === 0) return UserStatus.Active
    if (statusTab === 1) return UserStatus.Deactivated
    return null // All users - explicitly null to show all
  }, [statusTab])

  // Reset to page 1 when tab changes
  useEffect(() => {
    if (statusTab !== undefined) {
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
  }, [statusTab, reducerDispatch])

  const [debouncedSearchTerm] = useDebounce(searchValue, 500)

  const [searchUser, { loading: searchLoader }] = useSearchUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,

    variables: undefined,
    onError() {
      userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: [] })
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
      Alert.error(NO_USER_FOUND)
    },

    onCompleted(data) {
      if (data) {
        const {
          searchUser: { users }
        } = data

        reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
        userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: users || [] })
      }
    }
  })

  const [fetchUsers, { loading, refetch }] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,

    variables: undefined,
    onError() {
      userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: [] })
      reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
      reducerDispatch({ type: ActionType.SET_FILTER_DATA_COUNT, filterDataCount: 0 })
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      Alert.error(NO_USER_FOUND)
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllUsers } = data

        if (fetchAllUsers) {
          const { users, pagination, response } = fetchAllUsers
          const { status } = response || {}

          if (status === 404) {
            userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: [] })
          }

          if (users && !searchValue) {
            userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: users })
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
            totalCount && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
            reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
          }
        }
      }
    }
  })

  useEffect(() => {
    if (debouncedSearchTerm) {
      searchUser({
        variables: {
          searchUserInput: {
            searchTerm: debouncedSearchTerm || '',
            roles,
            status:
              tabStatus === UserStatus.Active
                ? SearchUserStatusFilter.Active
                : tabStatus === UserStatus.Deactivated
                ? SearchUserStatusFilter.Deactivated
                : SearchUserStatusFilter.All
          }
        }
      })
    } else {
      fetchUsers({
        variables: {
          userInput: {
            // Use tabStatus if statusTab prop is provided (from tabs), otherwise fall back to statusFilter from context
            // For tab-based filtering: tabStatus takes precedence (can be Active, Deactivated, or null for All)
            // For other usages: statusFilter from context is used (backward compatibility)
            status: statusTab !== undefined ? tabStatus : statusFilter || undefined,
            roles,
            from: dateFilter,
            to: toDateFilter,

            paginationOptions: {
              page: currentPage,
              limit: pageLimit
            }
          }
        }
      })
    }
    // eslint-disable-next-line
  }, [userRoles, pageLimit, currentPage, debouncedSearchTerm, statusTab])

  const isEmpty = usersData?.length === 0 && !loading && !searchLoader

  useEffect(() => {
    return () => {
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
      reducerDispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
      reducerDispatch({
        type: ActionType.SET_USER_ROLES,
        // Reset to undefined to search all users (including Staff) on cleanup
        userRoles: undefined
      })
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <SearchWithSort searchPlaceholder={SEARCH_PLACEHOLDER} sortByRole={rolesData || []} />

      <InviteNewUser refetch={refetch} state={userState} />

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 265px)' }}>
          <EmptyData title={NO_USER_FOUND} />
        </Box>
      ) : (
        <UserListing state={userState} dispatch={userDispatch} loading={loading} searchLoader={searchLoader} />
      )}

      <EditUser state={userState} dispatch={userDispatch} refetch={refetch} />
      <UploadCsv refetchUsers={refetch} />
    </>
  )
}
